/**
 * Meta platform constraints, §3.
 *
 * The single rule that shapes the whole architecture: in Instagram and
 * Messenger a business cannot initiate a conversation. The user always starts.
 * The business replies inside a 24-hour window; extending it is possible only
 * with limited tags for handing the conversation to a live agent.
 *
 * Everything below encodes the permitted entry map so the runtime can refuse
 * anything outside it at the type level rather than at review time.
 */

import type { EntryChannel } from './domain.js';

export interface EntryPointSpec {
  key: EntryChannel;
  label: string;
  initiatedBy: 'user' | 'business';
  paid: boolean;
  /** Opens a 24h standard messaging window. */
  opensWindow: boolean;
  /** Requires a recorded opt-in before the business may send. */
  requiresOptIn: boolean;
  description: string;
}

export const ENTRY_POINTS: EntryPointSpec[] = [
  {
    key: 'comment_to_dm',
    label: 'Comment-to-DM',
    initiatedBy: 'user',
    paid: false,
    opensWindow: true,
    requiresOptIn: false,
    description:
      'A private reply to a comment under a post or an ad. Exactly one private reply per comment. The main organic entry point.',
  },
  {
    key: 'story_reply',
    label: 'Story reply or mention',
    initiatedBy: 'user',
    paid: false,
    opensWindow: true,
    requiresOptIn: false,
    description: 'Opens the messaging window. The warmest inbound signal there is.',
  },
  {
    key: 'ice_breaker',
    label: 'Ice Breakers and persistent menu',
    initiatedBy: 'user',
    paid: false,
    opensWindow: true,
    requiresOptIn: false,
    description: 'A structured entry into qualification straight from the profile.',
  },
  {
    key: 'click_to_messenger',
    label: 'Click-to-Messenger ad',
    initiatedBy: 'user',
    paid: true,
    opensWindow: true,
    requiresOptIn: false,
    description: 'Paid, fully compliant and targetable — the legitimate cold-touch channel.',
  },
  {
    key: 'click_to_whatsapp',
    label: 'Click-to-WhatsApp ad',
    initiatedBy: 'user',
    paid: true,
    opensWindow: true,
    requiresOptIn: false,
    description: 'A paid entry into a WhatsApp conversation, opened by the user’s tap.',
  },
  {
    key: 'lead_ad',
    label: 'Lead Ads',
    initiatedBy: 'user',
    paid: true,
    opensWindow: false,
    requiresOptIn: false,
    description: 'A contact with explicit consent; any channel is permitted afterwards.',
  },
  {
    key: 'whatsapp_template',
    label: 'WhatsApp template',
    initiatedBy: 'business',
    paid: true,
    opensWindow: true,
    requiresOptIn: true,
    description:
      'The only outbound channel inside Meta. Priced per conversation; templates are reviewed.',
  },
  {
    key: 'email',
    label: 'Email or contact form',
    initiatedBy: 'business',
    paid: false,
    opensWindow: false,
    requiresOptIn: false,
    description: 'An external channel with its own rules (GDPR, CAN-SPAM). A bridge into the Meta channels.',
  },
];

export const ENTRY_POINT_BY_KEY = Object.fromEntries(
  ENTRY_POINTS.map((e) => [e.key, e]),
) as Record<EntryChannel, EntryPointSpec>;

/** Meta's standard messaging window. */
export const MESSAGING_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * The only message tag we use. HUMAN_AGENT extends the window for handing a
 * conversation to a live operator; we never use tags for marketing.
 */
export const ALLOWED_MESSAGE_TAG = 'HUMAN_AGENT' as const;

/** OAuth scopes requested through the official flow. */
export const META_SCOPES = [
  'pages_show_list',
  'pages_manage_metadata',
  'pages_messaging',
  'pages_read_engagement',
  'instagram_basic',
  'instagram_manage_messages',
  'instagram_manage_comments',
  'business_management',
] as const;

export type MetaWebhookObject = 'instagram' | 'page' | 'whatsapp_business_account';

export interface MetaWebhookEntry {
  id: string;
  time: number;
  messaging?: {
    sender: { id: string };
    recipient: { id: string };
    timestamp: number;
    message?: { mid: string; text?: string; is_echo?: boolean };
    postback?: { mid: string; title: string; payload: string };
  }[];
  changes?: {
    field: 'comments' | 'mentions' | 'messages';
    value: Record<string, unknown>;
  }[];
}

export interface MetaWebhookPayload {
  object: MetaWebhookObject;
  entry: MetaWebhookEntry[];
}
